import { useState, useEffect, useCallback } from "react";
import { authAxios } from "../../utils/auth";
import { AlertTriangle, ChevronLeft, ChevronRight, Filter } from "lucide-react";

const RULES = [
  { severity: "critical", label: "Traffic Blackout", desc: "0 views in 15min (business hours)" },
  { severity: "high", label: "Traffic Spike", desc: ">500% of hourly average" },
  { severity: "critical", label: "Error Rate Spike", desc: ">10 5xx errors in 5min" },
  { severity: "medium", label: "Signup Drought", desc: "0 signups for 24h" },
  { severity: "high", label: "Email Bounce Spike", desc: ">5% bounce rate in 24h" },
  { severity: "medium", label: "Conversion Collapse", desc: "<50% of 7-day avg conversion" },
];

const SEV_STYLE = {
  critical: "bg-red-500/15 text-red-400",
  high: "bg-amber-500/15 text-amber-400",
  medium: "bg-white/5 text-white/40",
};

export default function AnomaliesTab() {
  const [anomalies, setAnomalies] = useState(null);
  const [log, setLog] = useState(null);
  const [page, setPage] = useState(1);
  const [filterSeverity, setFilterSeverity] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const perPage = 15;

  useEffect(() => {
    authAxios.get("/analytics/command/anomalies")
      .then(res => setAnomalies(res.data))
      .catch(console.error);
  }, []);

  const fetchLog = useCallback(async () => {
    const params = new URLSearchParams({ page, per_page: perPage });
    if (filterSeverity) params.set("severity", filterSeverity);
    if (filterStatus) params.set("status", filterStatus);
    try {
      const { data } = await authAxios.get(`/analytics/command/anomalies/log?${params}`);
      setLog(data);
    } catch (e) { console.error(e); }
  }, [page, filterSeverity, filterStatus]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  return (
    <div className="space-y-6">
      {/* ── Active Rules ── */}
      <section className="rounded-xl border border-white/[0.06] bg-[#111113] p-5" data-testid="anomaly-rules">
        <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider mb-3">Active Rules</h2>
        <div className="space-y-1">
          {RULES.map(r => (
            <div key={r.label} className="flex items-center gap-2 text-[11px]">
              <span className={`px-1 py-0.5 rounded text-[9px] font-bold uppercase ${SEV_STYLE[r.severity]}`}>{r.severity}</span>
              <span className="text-white/60">{r.label}</span>
              <span className="text-white/25">— {r.desc}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Active Anomalies ── */}
      <section className={`rounded-xl border ${anomalies?.active_alerts?.length ? "border-red-500/20" : "border-white/[0.06]"} bg-[#111113] p-5`} data-testid="active-anomalies">
        <h2 className={`text-sm font-semibold uppercase tracking-wider mb-3 ${anomalies?.active_alerts?.length ? "text-red-400" : "text-white/50"}`}>
          Active Anomalies
        </h2>
        {anomalies?.active_alerts?.length ? (
          anomalies.active_alerts.map((a, i) => (
            <div key={i} className="flex items-center gap-3 py-2 text-xs border-b border-white/[0.03] last:border-0">
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${SEV_STYLE[a.severity] || SEV_STYLE.medium}`}>{a.severity}</span>
              <span className="text-white/70">{a.type?.replace(/_/g, " ")}</span>
              <span className="text-white/30">val: {a.current_value}</span>
              <span className="text-white/30 ml-auto">{a.triggered_at ? new Date(a.triggered_at).toLocaleString() : ""}</span>
              {a.email_sent && <span className="text-[10px] text-emerald-400/60">emailed</span>}
            </div>
          ))
        ) : (
          <p className="text-white/20 text-xs">No active anomalies</p>
        )}
      </section>

      {/* ── Anomaly Log ── */}
      <section className="rounded-xl border border-white/[0.06] bg-[#111113] p-5" data-testid="anomaly-log">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Anomaly Log
          </h2>
          <div className="flex items-center gap-2">
            <Filter className="w-3 h-3 text-white/30" />
            <select
              value={filterSeverity}
              onChange={e => { setFilterSeverity(e.target.value); setPage(1); }}
              className="bg-[#0a0a0a] border border-white/10 text-white/60 text-[11px] rounded px-2 py-1"
              data-testid="log-filter-severity"
            >
              <option value="">All Severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
            </select>
            <select
              value={filterStatus}
              onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
              className="bg-[#0a0a0a] border border-white/10 text-white/60 text-[11px] rounded px-2 py-1"
              data-testid="log-filter-status"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
        </div>

        {log?.items?.length ? (
          <>
            <table className="w-full text-xs" data-testid="anomaly-log-table">
              <thead>
                <tr className="text-white/30 text-left border-b border-white/[0.06]">
                  <th className="pb-2 font-medium">Severity</th>
                  <th className="pb-2 font-medium">Type</th>
                  <th className="pb-2 font-medium">Value</th>
                  <th className="pb-2 font-medium">Threshold</th>
                  <th className="pb-2 font-medium">Triggered</th>
                  <th className="pb-2 font-medium">Resolved</th>
                  <th className="pb-2 font-medium">Email</th>
                </tr>
              </thead>
              <tbody>
                {log.items.map((item, i) => (
                  <tr key={i} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                    <td className="py-2">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${SEV_STYLE[item.severity] || SEV_STYLE.medium}`}>
                        {item.severity}
                      </span>
                    </td>
                    <td className="py-2 text-white/70">{item.type?.replace(/_/g, " ")}</td>
                    <td className="py-2 text-white/50">{item.current_value ?? "—"}</td>
                    <td className="py-2 text-white/30">{item.threshold ?? "—"}</td>
                    <td className="py-2 text-white/40">{item.triggered_at ? new Date(item.triggered_at).toLocaleString() : "—"}</td>
                    <td className="py-2">
                      {item.resolved_at
                        ? <span className="text-emerald-400/60">{new Date(item.resolved_at).toLocaleString()}</span>
                        : <span className="text-red-400/60">active</span>
                      }
                    </td>
                    <td className="py-2">
                      {item.email_sent
                        ? <span className="text-emerald-400/60">sent</span>
                        : <span className="text-white/20">—</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/[0.06]">
              <span className="text-[11px] text-white/30">{log.total} total entries</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-2 py-1 rounded text-[11px] text-white/40 hover:text-white/70 disabled:opacity-30 bg-white/5"
                  data-testid="log-prev-page"
                >
                  <ChevronLeft className="w-3 h-3" />
                </button>
                <span className="text-[11px] text-white/40">
                  Page {log.page} of {log.total_pages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(log.total_pages, p + 1))}
                  disabled={page >= log.total_pages}
                  className="px-2 py-1 rounded text-[11px] text-white/40 hover:text-white/70 disabled:opacity-30 bg-white/5"
                  data-testid="log-next-page"
                >
                  <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <p className="text-white/20 text-xs">No anomalies logged yet</p>
        )}
      </section>
    </div>
  );
}
